import axiosLib, { AxiosError, AxiosRequestConfig } from "axios";
import { MaxRetriesError, SessionExpiredError } from "~/helpers/errors";
import {
  getJWT,
  setJWT,
  getRefreshToken,
  setRefreshToken,
  isTokenExpired,
} from "~/helpers/auth";
import { paths } from "~/router/paths";
import { signOutExcludedPaths } from "~/constants/paths";

import { endpointsToExcludeTheJWT } from "~/api/constants";
import { isAmazonS3Url } from "~/helpers/url-validation";
import { noteFailure, noteSuccess } from "~/helpers/maintenance";
import { reportApiFailure } from "~/helpers/datadogRum";
const apiURL: string = import.meta.env.VITE_API_URL;

/**
 * Raw fetch, deliberately not the `sendGet`/axios wrapper — a 401 through the
 * wrapper calls `forceSignOut()` and would log out every parked tab still
 * polling for recovery. `health_check/` is `@permission_classes([AllowAny])`
 * with no auth classes (commit f342211), so a plain 200 is expected; any
 * CORS-valid response at all proves the app server answered.
 *
 * `getURL` below does NOT auto-append a trailing slash, so the path is spelled
 * out with one here.
 */
export const confirmReadiness = async (): Promise<boolean> => {
  try {
    const res = await fetch(`${apiURL}/health_check/`, {
      cache: "no-store",
      signal: AbortSignal.timeout(5000),
    });
    return res.ok;
  } catch {
    return false;
  }
};

export const axios = axiosLib.create({
  timeout: 60000,
});

const getURL = (path: string) => `${apiURL}/${path}`;

// The one in-flight refresh. Every caller that needs a fresh access token
// awaits this same promise, so a page-load batch of N requests produces one
// refresh call rather than N (Monday ticket 12857922247).
let inFlightRefresh: Promise<string | null> | null = null;

const requestNewAccessToken = async (): Promise<string | null> => {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    // A bare axios call on purpose. Going through sendPost would re-enter
    // handleErrorWrapper, which retries and signs out on its own — making the
    // outcome of a refresh impossible to reason about from here, and nesting
    // one retry budget inside another.
    const res = await axios.post(getURL("auth/token/refresh/"), { refresh });
    const access = res?.data?.access;
    if (!access) return null;

    setJWT(access);
    return access;
  } catch {
    // Includes the 401 that means the refresh token is itself dead. Callers
    // treat null as "stop, this cannot be retried".
    return null;
  }
};

/**
 * Fresh access token, refreshing at most once across concurrent callers.
 *
 * Resolves to null when the refresh was refused, which is not retryable: the
 * caller's job is then to sign the user out, not to try again.
 */
export const ensureFreshToken = (): Promise<string | null> => {
  if (!inFlightRefresh) {
    inFlightRefresh = requestNewAccessToken().finally(() => {
      inFlightRefresh = null;
    });
  }

  return inFlightRefresh;
};

// Sign-in, registration, password reset and refresh carry no token, so a 401
// from them is the answer itself (wrong password, dead refresh token) and
// must reach the caller unchanged.
const isJWTExcludedEndpoint = (url?: string): boolean =>
  !!url && endpointsToExcludeTheJWT.some((endpoint) => url.includes(endpoint));

const handleRequest = async (config: AxiosRequestConfig): Promise<any> => {
  const isAuthEndpoint = isJWTExcludedEndpoint(config.url);
  const isS3Endpoint = isAmazonS3Url(config.url);

  if (isS3Endpoint || isAuthEndpoint) {
    return config;
  }

  let jwt = getJWT();

  // Refresh ahead of the request rather than after a 401 comes back. On a page
  // load every request in the batch reaches here holding the same expired
  // token; ensureFreshToken collapses them onto one refresh, so the batch
  // produces no 401s at all instead of one per request.
  if (isTokenExpired(jwt) && getRefreshToken()) {
    jwt = await ensureFreshToken();

    if (!jwt) {
      // The refresh token was refused. No retry can help, so stop here and
      // send the user to login rather than letting the batch spin on 401s.
      forceSignOut();
      throw new SessionExpiredError();
    }
  }

  if (jwt && config.headers) config.headers.Authorization = `Bearer ${jwt}`;
  return config;
};

axios.interceptors.request.use(handleRequest);

axios.interceptors.response.use(
  (response) => {
    noteSuccess();
    return response;
  },
  (error) => {
    // Only a 5xx or no answer is logged. The console.error this replaced was
    // picked up by Datadog for every status, so each expired session and each
    // user without a shop showed up as an error.
    reportApiFailure(error);
    // Fire and forget — detection must never delay or replace the rejection.
    // A no-status error (opaque infra-level failure) still reaches here
    // unchanged and falls through to the same auth branches below untouched.
    void noteFailure(error.response?.status, error.config?.url);
    return Promise.reject(error);
  }
);

export const forceSignOut = () => {
  // Always drop the invalid tokens, even when the redirect below is skipped,
  // so route guards and the next page load see the signed-out state.
  setJWT("");
  setRefreshToken("");

  if (
    signOutExcludedPaths.find((path) =>
      new RegExp(`^${path}`).test(window.location.pathname)
    )
  ) {
    return; // Already on a guest page; no redirect needed.
  }

  window.location.href = paths.auth.login;
};

const handleErrorWrapper = async <T = any>(
  next: () => Promise<T>,
  retries: number = 5
): Promise<T> => {
  if (retries <= 0) {
    forceSignOut();
    throw new MaxRetriesError("Max retries reached");
  }

  try {
    return await next();
  } catch (err: unknown) {
    if (err instanceof AxiosError) {
      if (err.response) {
        const { status } = err.response;
        const refresh = getRefreshToken();

        if (status === 402) {
          localStorage.setItem("plansModal", "true");
        }

        if (status === 401 && !isJWTExcludedEndpoint(err.config?.url)) {
          // A 401 from the refresh endpoint itself is terminal — the refresh
          // token is dead, so retrying only produces more 401s.
          if (err?.config?.url?.includes("/token/refresh/")) {
            forceSignOut();
            return Promise.reject(err);
          }

          if (refresh) {
            // Shared with every other request that raced into this branch, so
            // one expired token still costs exactly one refresh call.
            const access = await ensureFreshToken();

            if (!access) {
              forceSignOut();
              return Promise.reject(err);
            }

            return handleErrorWrapper(next, retries - 1);
          }

          // No refresh token to try: the session is over, whichever endpoint
          // answered. (A bare "/auth/" check used to exempt auth/user/ here,
          // so a dead session kept polling and logging instead of leaving.)
          forceSignOut();
          return Promise.reject(err);
        }
      }
    }

    return Promise.reject(err);
  }
};

export const sendGet = async (path: string, params?: Object): Promise<any> => {
  return handleErrorWrapper(async () => {
    return axios.get(getURL(path), { params });
  });
};

export const sendPost = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.post(getURL(path), params);
  });
};

export const sendPut = async (path: string, params?: Object) => {
  return handleErrorWrapper(async () => {
    return axios.put(getURL(path), params);
  });
};

export const sendDelete = async (path: string, params: Object) => {
  return handleErrorWrapper(async () => {
    return axios.delete(getURL(path), params);
  });
};

export const sendStreamingPost = async (
  path: string,
  params?: Object,
  onProgress?: (progress: number, data: any) => void
) => {
  return handleErrorWrapper(async () => {
    const response = await axios.post(getURL(path), params, {
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.event.target.responseText && onProgress) {
          const chunks = progressEvent.event.target.responseText
            .split("\n")
            .filter(Boolean);

          chunks.forEach((chunk: any) => {
            try {
              const data = JSON.parse(chunk);
              onProgress(data.progress || 0, data);
            } catch (e) {
              console.error("Error parsing progress chunk:", e);
            }
          });
        }
      },
    });
    return response.data;
  });
};
